/**
 * Giriş: the panel's own sign-in (ADR 0012), in the "Teknik Resim" / "Mavi Kopya" system: a
 * title block sheet on the drafting grid. Step 1 username and password, step 2 the 6 digit
 * code from the authenticator app, or one of the recovery codes. Errors are the server's
 * generic ones (no hint which part was wrong); a lockout says how long. Phone and desktop
 * share the layout; every control is at least 44 px.
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { type AuthApi, AuthError, authErrorText, type SignedIn } from '../api/auth.js';
import { Icon, LogoTile } from '../components/icons.js';

/** Why the login screen shows: the session ended, the user logged out, or no answer at start. */
export type GirisNotice = 'expired' | 'logged-out' | 'offline' | null;

const NOTICES: Record<Exclude<GirisNotice, null>, string> = {
  expired: 'Oturumun sona erdi. Tekrar giriş yapınca kaldığın ekrana dönersin.',
  'logged-out': 'Çıkış yaptın. Bu cihazdaki oturum kapandı.',
  offline: 'Sunucuya ulaşılamadı. Bağlantını kontrol edip giriş yap.',
};

type Step = { name: 'password' } | { name: 'code'; challenge: string; recovery: boolean };

export function Giris({
  auth,
  notice,
  onSignedIn,
}: {
  auth: AuthApi;
  notice: GirisNotice;
  onSignedIn: (result: SignedIn) => void;
}) {
  const [step, setStep] = useState<Step>({ name: 'password' });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();
  const errorId = useId();
  const hintId = useId();
  const firstField = useRef<HTMLInputElement | null>(null);
  const codeField = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (step.name === 'password') firstField.current?.focus();
    else codeField.current?.focus();
  }, [step]);

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || username.trim() === '' || password === '') return;
    setBusy(true);
    setError(null);
    try {
      const result = await auth.login(username.trim(), password);
      setPassword('');
      if (result.kind === 'done') onSignedIn({ session: result.session, recoveryCodesLeft: null });
      else {
        setCode('');
        setStep({ name: 'code', challenge: result.challenge, recovery: false });
      }
    } catch (failure) {
      setError(authErrorText(failure));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || step.name !== 'code') return;
    const value = code.trim();
    if (value === '') return;
    setBusy(true);
    setError(null);
    try {
      const result = await auth.verify(step.challenge, step.recovery ? { recoveryCode: value } : { code: value });
      onSignedIn(result);
    } catch (failure) {
      setCode('');
      setError(authErrorText(failure));
      if (failure instanceof AuthError && failure.code === 'login_expired') {
        setStep({ name: 'password' });
      }
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setStep({ name: 'password' });
    setCode('');
    setError(null);
  };

  const toggleRecovery = () => {
    if (step.name !== 'code') return;
    setStep({ ...step, recovery: !step.recovery });
    setCode('');
    setError(null);
  };

  const described = [error === null ? null : errorId, hintId].filter(Boolean).join(' ');

  return (
    <main className="login">
      <section className="login__sheet" aria-labelledby={headingId}>
        <header className="login__head">
          <LogoTile size={36} />
          <div>
            <h1 id={headingId} className="login__title">
              Twin
            </h1>
            <p className="login__sub">CadGaragePro · panel girişi</p>
          </div>
        </header>

        {notice !== null && error === null ? (
          <p className="login__notice" role="status">
            {NOTICES[notice]}
          </p>
        ) : null}
        {error !== null ? (
          <p id={errorId} className="login__alert" role="alert">
            <Icon name="info" size={16} />
            <span>{error}</span>
          </p>
        ) : null}

        {step.name === 'password' ? (
          <form className="login__form" onSubmit={submitPassword} noValidate aria-busy={busy}>
            <p id={hintId} className="login__step key">
              Adım 1 / 2 · kimlik
            </p>
            <label className="login__field">
              <span className="login__label">Kullanıcı adı</span>
              <input
                ref={firstField}
                className="field__input login__input"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="email"
                spellCheck={false}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                aria-invalid={error !== null}
                aria-describedby={described}
                required
              />
            </label>
            <div className="login__field">
              <label className="login__label" htmlFor={`${headingId}-password`}>
                Parola
              </label>
              <div className="login__password">
                <input
                  id={`${headingId}-password`}
                  className="field__input login__input"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-invalid={error !== null}
                  aria-describedby={described}
                  required
                />
                <button
                  type="button"
                  className="login__reveal"
                  aria-label={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((shown) => !shown)}
                >
                  <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="btn btn--primary login__submit"
              disabled={busy || username.trim() === '' || password === ''}
            >
              {busy ? 'Kontrol ediliyor…' : 'Devam et'}
            </button>
          </form>
        ) : (
          <form className="login__form" onSubmit={submitCode} noValidate aria-busy={busy}>
            <p className="login__step key">Adım 2 / 2 · {step.recovery ? 'kurtarma kodu' : 'doğrulama kodu'}</p>
            <div className="login__field">
              <label className="login__label" htmlFor={`${headingId}-code`}>
                {step.recovery ? 'Kurtarma kodu' : '6 haneli kod'}
              </label>
              {step.recovery ? (
                <input
                  id={`${headingId}-code`}
                  ref={codeField}
                  key="recovery"
                  className="field__input login__input login__input--mono"
                  name="recovery-code"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={20}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  aria-invalid={error !== null}
                  aria-describedby={described}
                />
              ) : (
                <input
                  id={`${headingId}-code`}
                  ref={codeField}
                  key="totp"
                  className="field__input login__input login__code"
                  name="one-time-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  aria-invalid={error !== null}
                  aria-describedby={described}
                />
              )}
            </div>
            <p id={hintId} className="login__hint">
              {step.recovery
                ? 'Şifre kasandaki 12 karakterlik kodlardan birini yaz. Her kod bir kez çalışır.'
                : 'Telefonundaki doğrulayıcı uygulamada Twin için görünen kodu yaz.'}
            </p>
            <button
              type="submit"
              className="btn btn--primary login__submit"
              disabled={busy || (step.recovery ? code.trim().length < 12 : code.length !== 6)}
            >
              {busy ? 'Kontrol ediliyor…' : 'Giriş yap'}
            </button>
            <div className="login__links">
              <button type="button" className="text-btn" onClick={back}>
                <Icon name="chevron-left" size={16} /> Geri
              </button>
              <button type="button" className="text-btn" onClick={toggleRecovery}>
                {step.recovery ? 'Doğrulama kodu kullan' : 'Telefonum yanımda değil'}
              </button>
            </div>
          </form>
        )}

        <footer className="login__foot">
          <span className="key">Yetki · sahip</span>
          <span className="login__foot-text">Her giriş Telegram'a bildirilir.</span>
        </footer>
      </section>
    </main>
  );
}
